import {
  Alert,
  Box,
  Button,
  Checkbox,
  Fieldset,
  Group,
  LoadingOverlay,
  NumberInput,
  Select,
  Space,
  TextInput
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useFocusWithin } from "@mantine/hooks";
import { IconAlertCircle } from "@tabler/icons-react";
import { SchemaObjectDescription } from "yup";
import CepInput from "@/components/cep-input";
import CNPJInput from "@/components/cnpj-input";
import TelInput from "@/components/tel-input";
import { extractTextInputPropertiesFromFormSchema } from "@/utils/form";
import useLicenciadaForm from "./useLicenciadaForm";
import { TLicenciadaForm } from "../../firestore";
import licenciadaSchema, { estadosData } from "../../schema/licenciada-schema";

const FOCUS_SECTION_COLOR = "var(--mantine-color-blue-light)";

const LicenciadaForm = ({ licenciada, onSuccess }: { licenciada: TLicenciadaForm; onSuccess: () => void }) => {
  const { onSubmit, form, isPending } = useLicenciadaForm(licenciada, {
    onSuccess
  });

  const { ref: referenceFieldsetLicenciada, focused: focusedFieldsetLicenciada } = useFocusWithin();
  const { ref: referenceFieldsetResponsavel, focused: focusedFieldsetResponsavel } = useFocusWithin();
  const { ref: referenceFieldsetEndereco, focused: focusedFieldsetEndereco } = useFocusWithin();
  const { ref: referenceFieldsetAcessos, focused: focusedFieldsetAcessos } = useFocusWithin();
  const { ref: referenceFieldsetAutorizacoes, focused: focusedFieldsetAutorizacoes } = useFocusWithin();

  const licenciadaFieldsDescriptor = licenciadaSchema.describe();
  const responsavelDescriptor = licenciadaSchema.fields.responsavel.describe() as SchemaObjectDescription;
  const enderecoDescriptor = licenciadaSchema.fields.endereco.describe() as SchemaObjectDescription;
  const acessosDescriptor = licenciadaSchema.fields.acessos.describe() as SchemaObjectDescription;

  const genericError = form.errors[""];

  return (
    <form onSubmit={onSubmit}>
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        <Fieldset
          legend="Dados da Licenciada"
          mb="sm"
          ref={referenceFieldsetLicenciada}
          style={{
            backgroundColor: focusedFieldsetLicenciada ? FOCUS_SECTION_COLOR : "transparent"
          }}
        >
          <Group gap="md">
            <CNPJInput
              data-autofocus
              w="160px"
              {...form.getInputProps("cnpj")}
              {...extractTextInputPropertiesFromFormSchema(licenciadaFieldsDescriptor, "cnpj")}
            />
            <TextInput
              type="text"
              style={{ flex: 1 }}
              {...form.getInputProps("razaoSocial")}
              {...extractTextInputPropertiesFromFormSchema(licenciadaFieldsDescriptor, "razaoSocial")}
            />
          </Group>
          <Group gap="md">
            <TextInput
              type="text"
              style={{ flex: 1 }}
              {...form.getInputProps("nomeFantasia")}
              {...extractTextInputPropertiesFromFormSchema(licenciadaFieldsDescriptor, "nomeFantasia")}
            />
            <TextInput
              type="email"
              {...form.getInputProps("email")}
              {...extractTextInputPropertiesFromFormSchema(licenciadaFieldsDescriptor, "email")}
            />
            <TelInput
              w="140px"
              {...form.getInputProps("telefone")}
              {...extractTextInputPropertiesFromFormSchema(licenciadaFieldsDescriptor, "telefone")}
            />
          </Group>
          <Group gap="md">
            <DateInput
              valueFormat="DD/MM/YYYY"
              {...form.getInputProps("contratoInicio")}
              {...extractTextInputPropertiesFromFormSchema(licenciadaFieldsDescriptor, "contratoInicio")}
            />
            <DateInput
              valueFormat="DD/MM/YYYY"
              {...form.getInputProps("contratoFim")}
              {...extractTextInputPropertiesFromFormSchema(licenciadaFieldsDescriptor, "contratoFim")}
            />
          </Group>
        </Fieldset>

        <Fieldset
          legend="Dados do Responsável"
          mb="sm"
          ref={referenceFieldsetResponsavel}
          style={{
            backgroundColor: focusedFieldsetResponsavel ? FOCUS_SECTION_COLOR : "transparent"
          }}
        >
          <Group gap="md">
            <TextInput
              type="text"
              style={{ flex: 1 }}
              {...form.getInputProps("responsavel.nome")}
              {...extractTextInputPropertiesFromFormSchema(responsavelDescriptor, "nome")}
            />
            <TextInput
              type="text"
              style={{ flex: 1 }}
              {...form.getInputProps("responsavel.email")}
              {...extractTextInputPropertiesFromFormSchema(responsavelDescriptor, "email")}
            />
            <TelInput
              w="140px"
              {...form.getInputProps("responsavel.telefone")}
              {...extractTextInputPropertiesFromFormSchema(responsavelDescriptor, "telefone")}
            />
          </Group>
        </Fieldset>

        <Fieldset
          legend="Endereço"
          mb="sm"
          ref={referenceFieldsetEndereco}
          style={{
            backgroundColor: focusedFieldsetEndereco ? FOCUS_SECTION_COLOR : "transparent"
          }}
        >
          <Group gap="md">
            <CepInput
              w="100px"
              {...form.getInputProps("endereco.cep")}
              {...extractTextInputPropertiesFromFormSchema(enderecoDescriptor, "cep")}
            />
            <TextInput
              type="text"
              style={{ flex: 1 }}
              {...form.getInputProps("endereco.logradouro")}
              {...extractTextInputPropertiesFromFormSchema(enderecoDescriptor, "logradouro")}
            />
            <TextInput
              type="text"
              w="100px"
              {...form.getInputProps("endereco.numero")}
              {...extractTextInputPropertiesFromFormSchema(enderecoDescriptor, "numero")}
            />
            <TextInput
              type="text"
              w="150px"
              {...form.getInputProps("endereco.complemento")}
              {...extractTextInputPropertiesFromFormSchema(enderecoDescriptor, "complemento")}
            />
          </Group>
          <Group gap="md">
            <TextInput
              type="text"
              style={{ flex: 1 }}
              {...form.getInputProps("endereco.bairro")}
              {...extractTextInputPropertiesFromFormSchema(enderecoDescriptor, "bairro")}
            />
            <TextInput
              type="text"
              style={{ flex: 1 }}
              {...form.getInputProps("endereco.cidade")}
              {...extractTextInputPropertiesFromFormSchema(enderecoDescriptor, "cidade")}
            />
            <Select
              data={estadosData}
              style={{ flex: 1 }}
              {...form.getInputProps("endereco.estado")}
              {...extractTextInputPropertiesFromFormSchema(enderecoDescriptor, "estado")}
            />
          </Group>
        </Fieldset>

        <Fieldset
          legend="Acessos"
          mb="sm"
          ref={referenceFieldsetAcessos}
          style={{
            backgroundColor: focusedFieldsetAcessos ? FOCUS_SECTION_COLOR : "transparent"
          }}
        >
          <Group gap="md">
            <NumberInput
              min={0}
              {...form.getInputProps("acessos.admin")}
              {...extractTextInputPropertiesFromFormSchema(acessosDescriptor, "admin")}
            />
            <NumberInput
              min={0}
              {...form.getInputProps("acessos.member")}
              {...extractTextInputPropertiesFromFormSchema(acessosDescriptor, "member")}
            />
            <NumberInput
              min={0}
              {...form.getInputProps("acessos.team")}
              {...extractTextInputPropertiesFromFormSchema(acessosDescriptor, "team")}
            />
            <NumberInput
              min={0}
              {...form.getInputProps("acessos.master")}
              {...extractTextInputPropertiesFromFormSchema(acessosDescriptor, "master")}
            />
            <NumberInput
              min={0}
              {...form.getInputProps("acessos.collaborator")}
              {...extractTextInputPropertiesFromFormSchema(acessosDescriptor, "collaborator")}
            />
          </Group>
        </Fieldset>

        <Fieldset
          legend="Autorizações"
          mb="sm"
          ref={referenceFieldsetAutorizacoes}
          style={{
            backgroundColor: focusedFieldsetAutorizacoes ? FOCUS_SECTION_COLOR : "transparent"
          }}
        >
          <Group gap="md">
            <Checkbox label="Gestão" {...form.getInputProps("autorizacoes.gestao", { type: "checkbox" })} />
            <Checkbox label="Diagnóstico" {...form.getInputProps("autorizacoes.diagnostico", { type: "checkbox" })} />
            <Checkbox label="Incentivo" {...form.getInputProps("autorizacoes.incentivo", { type: "checkbox" })} />
            <Checkbox
              checked={true}
              disabled
              label="Configurações"
              {...form.getInputProps("autorizacoes.configuracoes", { type: "checkbox" })}
            />
          </Group>
        </Fieldset>
      </Box>
      <Space h="md" />
      <Group justify={"flex-end"} align="center">
        <Group justify={genericError ? "space-between" : "flex-end"} mt="md" align="center">
          {genericError && (
            <Alert icon={<IconAlertCircle size="1rem" />} color="red">
              {genericError}
            </Alert>
          )}

          <Button type="submit" loading={isPending}>
            Salvar
          </Button>
        </Group>
      </Group>
    </form>
  );
};

export default LicenciadaForm;
